import React, { useCallback, useState } from 'react'
import Header from '../Header/Header.js';
import SideBar from './SideBar.js';
import { useQuery } from '@apollo/client';
import { GET_DASHBOARD_SUMMERY } from '../graphql/Query/Signin.js';
import StateWiseBarChart from '../components/Charts/StateWiseBarChart.jsx';
import ShipmentStatusPieChart from '../components/Charts/ShipmentStatusPieChart.jsx';
import DeliveredPercentageChart from '../components/Charts/DeliveredPercentageChart.jsx';
import PaymentTypePieChart from '../components/Charts/PaymentTypePieChart.jsx';
import DateFilter, { getDateRange } from '../Common/DateRange.js';

const Dashboard = () => {
    // We'll store the final date range in state, as provided by DateFilter
    const [finalRange, setFinalRange] = useState({
        filterType: "Last 30 Days",
        startDate: new Date(),
        endDate: new Date(),
    });

    // Destructure the final range to pass to the query
    const { filterType, startDate, endDate } = finalRange;

    // Convert startDate and endDate to ISO
    const isoStart = startDate?.toISOString();
    const isoEnd = endDate?.toISOString();

    const { data } = useQuery(GET_DASHBOARD_SUMMERY, {
        variables: {
            filter: {
                dateRange: {
                    startDate: isoStart,
                    endDate: isoEnd,
                }
            }
        },
        fetchPolicy: "cache-and-network"
    });
    const handleDateChange = useCallback((range) => {
        setFinalRange(range);
    }, []);

    return (
        <>
            <Header />
            <div className="flex bg-slate-100 min-h-screen">
                <SideBar />

                <div className="flex-1 md:p-4 py-4 sm:p-6 ml-16 sm:ml-16">

                    {/* Top Info Boxes */}
                    <div className="bg-slate-100 overflow-x-hidden">
                        <div className="flex flex-col md:flex-row justify-between p-4 bg-white md:rounded-md border border-slate-200">
                            <div className="flex flex-col sm:flex-row sm:items-center space-y-1 sm:space-y-0 sm:space-x-3">
                                <h1 className="text-xl sm:text-2xl font-bold">Dashboard</h1>
                                <p className="text-sm sm:text-base font-semibold text-slate-400">Analytics Overview</p>
                            </div>

                            <DateFilter onDateChange={handleDateChange} defaultFilterType="Last 30 Days" />
                        </div>

                        {/* <div className="mt-4 p-3 bg-white border border-l-4 border-red-400 text-red-600 font-semibold text-center md:rounded-md text-sm sm:text-base">
                            Attention: Weight Discrepancy Data Will Be Available On The New Tab Starting January 13th, 2025.
                            <br /> <p href="#" className="text-blue-500 underline ml-1">Click Here</p>
                        </div> */}

                        {/* KPI Cards */}
                        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 h-full lg:grid-cols-12 gap-6">
                            {/* Left Column: Metrics Cards */}
                            <div className="space-y-7 sm:col-span-2 md:col-span-12 lg:col-span-4">
                                {/* Total Shipments */}
                                <div className="p-6 space-y-1 bg-white border border-slate-200 rounded-lg text-center">
                                    <h2 className="font-semibold">Total Shipments</h2>
                                    <p className="text-gray-600 text-3xl font-bold">{data?.getDashboardData?.totalShipments}</p>
                                    <p className="text-green-600">Total forward shipments</p>
                                </div>

                                {/* Delivered Orders */}
                                <div className="p-6 space-y-1 bg-white border border-slate-200 rounded-lg text-center">
                                    <h2 className="font-semibold">Delivered Orders</h2>
                                    <p className="text-gray-600 text-3xl font-bold">{data?.getDashboardData?.deliveredOrders}</p>
                                    <p className="text-green-600">Total forward delivered orders</p>
                                </div>

                                {/* Revenue */}
                                <div className="p-6 space-y-1 bg-white border border-slate-200 rounded-lg text-center">
                                    <h2 className="font-semibold">Revenue</h2>
                                    <p className="text-gray-600 text-3xl font-bold">₹{data?.getDashboardData?.revenue}</p>
                                    <p className="text-green-600">Value of Delivered Orders</p>
                                </div>

                                {/* Revenue */}
                                <div className="py-6 px-2 space-y-4 bg-white border border-slate-200 rounded-lg text-center">
                                    <div className="flex flex-col md:flex-row items-center justify-between md:space-x-6">
                                        {/* COD Revenue - 1 */}
                                        <div className="flex-1 text-center">
                                            <h2 className="font-semibold">COD Revenue</h2>
                                            <p className="text-gray-600 text-3xl font-bold">₹{data?.getDashboardData?.codRevenue}</p>
                                            <p className="text-green-600">Delivered Order Value</p>
                                        </div>

                                        {/* Vertical Divider */}
                                        <div className="border-l hidden md:block border-gray-300 h-16"></div>

                                        {/* COD Revenue - 2 */}
                                        <div className="flex-1 text-center">
                                            <h2 className="font-semibold">Prepaid Revenue</h2>
                                            <p className="text-gray-600 text-3xl font-bold">₹{data?.getDashboardData?.prepaidRevenue}</p>
                                            <p className="text-green-600">Delivered Order Value</p>
                                        </div>
                                    </div>
                                </div>

                            </div>

                            {/* Right Column: Charts */}
                            <div className="lg:col-span-8 md:col-span-12">

                                <div className="bg-white border border-slate-200 rounded-lg p-6 flex flex-col justify-between md:flex-row items-center">
                                    <div className='mb-4'>
                                        <h2 className="font-semibold text-2xl mb-2">Payment Wise Load</h2>
                                        <p className='text-slate-400 mt-2'>
                                            The Payment Wise Load section provides an insightful breakdown of shipments categorized by different payment methods. This section is crucial for understanding how shipments are processed and how customers prefer to make payments.
                                        </p>
                                    </div>

                                    {/* Show blank graph if data is empty */}
                                    <PaymentTypePieChart paymentWiseData={data?.getDashboardData?.paymentWise || []} />
                                </div>

                                {/* <div className="bg-white border border-slate-200 rounded-lg p-6 flex flex-col justify-between md:flex-row items-center">

                                    <div className='mb-4'>
                                        <h2 className="font-semibold text-2xl mb-2">Payment Wise Load</h2>
                                        <p className='text-slate-400 mt-2'>The Payment Wise Load section provides an insightful breakdown of shipments categorized by different payment methods. This section is crucial for understanding how shipments are processed and how customers prefer to make payments.</p>

                                    </div>
                                    {data?.getDashboardData?.paymentWise?.length > 0 ? (
                                        <PaymentTypePieChart paymentWiseData={data?.getDashboardData?.paymentWise} />
                                    ) : (
                                        <p className="text-center">No payment data available</p>
                                    )}
                                </div> */}
                                {/* Shipment Status Pie Chart */}

                                <div className="bg-white border mt-4 border-slate-200 rounded-lg p-6 flex flex-col justify-between md:flex-row items-center">
                                    <div className='mb-4'>
                                        <h2 className="font-semibold text-3xl mb-2">Order Status Updates: Tracking Your Package</h2>
                                        <p className='text-slate-400 mt-2'>
                                            Your package is currently In Transit, making its way to the delivery destination.
                                            If it's Delivered, it has successfully reached you.
                                            If it's still Pending Pickup, it's waiting to be collected by the courier.
                                            Once an order is Booked, it has been confirmed and will be shipped soon.
                                        </p>
                                    </div>
                                    <div>
                                        {/* {data?.getDashboardData?.statusWise?.length > 0 ? ( */}
                                        <ShipmentStatusPieChart statusWiseData={data?.getDashboardData?.statusWise} />
                                        {/* // ) : null} */}
                                    </div>
                                </div>

                                {/* <div className="bg-white border mt-4 border-slate-200 rounded-lg p-6 flex flex-col justify-between md:flex-row items-center">
                                    <div className='mb-4'>
                                        <h2 className="font-semibold text-3xl mb-2">Order Status Updates: Tracking Your Package</h2>
                                        <p className='text-slate-400 mt-2'>Your package is currently In Transit, making its way to the delivery destination. If it's Delivered, it has successfully reached you. If it's still Pending Pickup, it's waiting to be collected by the courier. Once an order is Booked, it has been confirmed and will be shipped soon.</p>

                                    </div>
                                    <div>
                                        {data?.getDashboardData?.statusWise?.length > 0 ? (
                                            <ShipmentStatusPieChart statusWiseData={data?.getDashboardData?.statusWise} />
                                        ) : (
                                            <p>No status data available</p>
                                        )}</div>
                                </div> */}

                            </div>

                        </div>

                        {/* Middle Section: Delivered Percentage */}
                        <div className="mt-6 grid border border-slate-200 grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="p-8 bg-white rounded-lg flex flex-col items-center w-full">
                                <h2 className="text-3xl mb-4 font-semibold">Delivered Percentage</h2>
                                <p className="text-gray-500 mt-2 text-sm sm:text-base">
                                    Delivered Percentage is a key metric used in logistics, e-commerce, and courier services to measure the success rate of order deliveries. It indicates the proportion of orders that have been successfully delivered compared to the total number of orders placed.
                                </p>
                                <div className="mt-6 flex items-center justify-center">
                                    {/* Replace the static ring with the radial chart */}
                                    <DeliveredPercentageChart
                                        percentage={data?.getDashboardData?.deliveredPercentage || 0}
                                    />
                                </div>
                            </div>

                            {/* State Wise Load (Bar Chart) */}

                            <div className="p-6 bg-white rounded-lg">
                                <h2 className="font-semibold text-3xl text-center mb-2">State Wise Load</h2>
                                <p className="text-gray-500 mt-2 text-sm sm:text-base">
                                    State Wise Load represents the distribution of shipments, deliveries, or orders across different states.
                                    It provides insights into regional demand, shipment volumes, and delivery efficiency.
                                    Businesses use this data to optimize logistics, forecast demand, and improve supply chain management.
                                </p>

                                {data?.getDashboardData?.stateWise?.length > 0 && (
                                    <StateWiseBarChart stateWiseData={data?.getDashboardData?.stateWise} />
                                )}
                            </div>

                            {/* <div className="p-6 bg-white rounded-lg">
                                <h2 className="font-semibold text-3xl text-center mb-2">State Wise Load</h2>
                                <p className="text-gray-500 mt-2 text-sm sm:text-base">
                                    State Wise Load represents the distribution of shipments, deliveries, or orders across different states. It provides insights into regional demand, shipment volumes, and delivery efficiency. Businesses use this data to optimize logistics, forecast demand, and improve supply chain management.
                                </p>

                                {data?.getDashboardData?.stateWise?.length > 0 ? (
                                    <StateWiseBarChart stateWiseData={data?.getDashboardData?.stateWise} />
                                ) : (
                                    <p className="text-center">No state data available</p>
                                )}
                            </div> */}
                        </div>

                    </div>

                </div>
            </div>
        </>
    );
};

export default Dashboard;

